import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, ArrowRight, CheckCircle } from 'lucide-react';

const ContactPage = () => {
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    type: '',
    message: '',
    accord: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
            <Link to="/" className="hover:text-orange-400 transition-colors">Accueil</Link>
            <span>/</span>
            <span className="text-white">Contact & Devis</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white mb-2">Contact & Devis Gratuit</h1>
          <p className="text-gray-400">Nos conseillers vous répondent sous 24h ouvrées.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
              <h3 className="font-bold text-gray-900 mb-5 text-lg">Nos coordonnées</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin size={16} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Notre usine</p>
                    <p className="text-gray-500 text-sm">Gardanne, Bouches du Rhône (13)<br />Entre Aix-en-Provence et Marseille</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone size={16} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Téléphone</p>
                    <p className="text-gray-500 text-sm">09 70 70 30 38</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail size={16} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Email</p>
                    <p className="text-gray-500 text-sm">contact@stores-shop.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock size={16} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Horaires</p>
                    <p className="text-gray-500 text-sm">Lun – Ven : 9h00 – 12h00 / 14h00 – 17h30</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-orange-500 rounded-2xl p-6 text-white">
              <h4 className="font-bold mb-2">Devis en 24h</h4>
              <p className="text-orange-100 text-sm mb-3">Tous nos stores sont fabriqués sur mesure. Envoyez-nous vos mesures et nous vous établirons un devis personnalisé sous 24h ouvrées.</p>
              <div className="space-y-2">
                {['Réponse sous 24h', 'Devis gratuit', 'Sans engagement'].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle size={14} />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-orange-500" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Demande envoyée !</h3>
                  <p className="text-gray-500 mb-6">Merci pour votre demande. Nous vous répondrons dans les 24h ouvrées.</p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-orange-500 hover:underline font-semibold"
                  >
                    Envoyer une nouvelle demande
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-black text-gray-900 mb-6">Demander un devis gratuit</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prénom *</label>
                        <input
                          type="text"
                          name="prenom"
                          value={form.prenom}
                          onChange={handleChange}
                          required
                          placeholder="Votre prénom"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom *</label>
                        <input
                          type="text"
                          name="nom"
                          value={form.nom}
                          onChange={handleChange}
                          required
                          placeholder="Votre nom"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="votre@email.com"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone</label>
                        <input
                          type="tel"
                          name="telephone"
                          value={form.telephone}
                          onChange={handleChange}
                          placeholder="06 XX XX XX XX"
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type de produit</label>
                      <select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all bg-white"
                      >
                        <option value="">Sélectionner un produit</option>
                        <option>Store Banne Monobloc (Model M)</option>
                        <option>Store Banne Premier Prix (Model E)</option>
                        <option>Store de Véranda (Model V)</option>
                        <option>Pergola Bioclimatique (Model PB)</option>
                        <option>Store à Projection (Model I)</option>
                        <option>Store Banne Coffre (Model C)</option>
                        <option>Autre</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">Votre message / description du projet *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Décrivez votre projet : dimensions souhaitées, type d'installation, couleur, motorisation..."
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all resize-none"
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="accord"
                        name="accord"
                        checked={form.accord}
                        onChange={handleChange}
                        required
                        className="mt-1 w-4 h-4 accent-orange-500"
                      />
                      <label htmlFor="accord" className="text-sm text-gray-600">
                        J'accepte que mes données soient traitées pour répondre à ma demande de devis. *
                      </label>
                    </div>
                    <button
                      type="submit"
                      className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-orange-200 text-base"
                    >
                      Envoyer ma demande de devis
                      <ArrowRight size={18} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
